/**
 * PickSlip — iter86.97 One UI Phase 3 primitive (reworked iter87.0).
 *
 * The pick slip is STICKY inside its pick panel, not viewport-fixed:
 * it rides the bottom of the screen while the panel is in view, then
 * docks at the panel's last row and scrolls away with it (operator
 * fix — the old fixed slip kept floating over unrelated content).
 * Live count, tap-to-remove chips, one Submit.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const ACCENTS = {
    cyber: { text: "text-cyber", btn: "bg-cyber text-black border-black" },
    volt: { text: "text-volt", btn: "bg-volt text-black border-black" },
};

export const PickSlip = ({
    visible,
    count,
    required,
    label,
    chips = [],
    onRemoveChip,
    submitLabel,
    onSubmit,
    submitDisabled,
    submitTestId = "pick-slip-submit",
    accent = "cyber",
    testId = "pick-slip",
    // iter87.406 (user-requested) — when the caller hands over a modal's
    // floating footer slot DOM node (the same flush-against-the-bottom-
    // border pattern MatchdayBoostModal already uses), render as a
    // plain flush bar through a portal instead of the "sticky inside a
    // scrolling panel + negative-margin" trick below, which only ever
    // cancelled out a padding value tuned for one specific ancestor and
    // left a gap (or overflow) against any other.
    portalTarget = null,
}) => {
    const [expanded, setExpanded] = useState(false);
    if (!visible) return null;
    const tone = ACCENTS[accent] || ACCENTS.cyber;
    const done = required > 0 && count >= required;

    const content = (
        <div
            data-testid={testId}
            className={portalTarget ? "shrink-0" : "sticky bottom-0 z-20 -mx-5 -mb-5 mt-4"}
        >
            <div
                className={
                    portalTarget
                        ? "glass border-t border-white/10 px-4 sm:px-6 py-3"
                        : "glass border-t border-white/10 rounded-b-2xl px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
                }
            >
                {!portalTarget && (
                    <button
                        type="button"
                        aria-label="Toggle pick details"
                        data-testid={`${testId}-toggle`}
                        onClick={() => setExpanded((e) => !e)}
                        className="w-full lg:hidden"
                    >
                        <span className="block mx-auto mb-1.5 h-1 w-8 rounded-full bg-white/20" />
                    </button>
                )}
                <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="font-display font-black text-sm uppercase tracking-tight leading-tight">
                            <span className={done ? "text-neon" : tone.text} data-testid={`${testId}-count`}>
                                {count}/{required}
                            </span>{" "}
                            {label}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid={submitTestId}
                        onClick={onSubmit}
                        disabled={submitDisabled}
                        className={`shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none ${tone.btn}`}
                    >
                        {submitLabel}
                    </button>
                </div>
                {chips.length > 0 && (
                    <div
                        data-testid={`${testId}-chips`}
                        className={`${portalTarget || expanded ? "flex" : "hidden"} lg:flex flex-wrap gap-1.5 mt-2 max-h-28 overflow-y-auto`}
                    >
                        {chips.map((c) => (
                            <span
                                key={c.id}
                                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                            >
                                {c.label}
                                {onRemoveChip && (
                                    <button
                                        type="button"
                                        aria-label={`Remove ${c.label}`}
                                        data-testid={`${testId}-chip-remove-${c.id}`}
                                        onClick={() => onRemoveChip(c.id)}
                                        className="text-zinc-400 hover:text-heat transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    return portalTarget ? createPortal(content, portalTarget) : content;
};

export default PickSlip;
